import { SuggestedSettingsEnum, SuggestedSettingsType } from '@lp/features/RoutePlanner/types';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { CaseClinicalSpecialistResource, CaseOCSConsoleResource, CaseResource } from '@/api/apis/case';

const SUGGESTED_SETTINGS_ERRORS_MAP: Partial<Record<SuggestedSettingsEnum, string>> = {
  [SuggestedSettingsEnum.RouteType]: 'Select Route Type',
  [SuggestedSettingsEnum.OrganTransportationType]: 'Select DH-TXC transportation type',
  [SuggestedSettingsEnum.OR]: 'Select OR preferences',
  [SuggestedSettingsEnum.StartLocation]: 'Select Start Location',
  [SuggestedSettingsEnum.StartTime]: 'Select Start Time',
  [SuggestedSettingsEnum.ArrivalHub]: 'Select Arrival Hub',
};

const START_TIME_IN_PAST_ERROR = 'Start Time is in the past.';

const FULL_ROUTE_REQUIRED_SETTINGS = [
  SuggestedSettingsEnum.RouteType,
  SuggestedSettingsEnum.OR,
  SuggestedSettingsEnum.OrganTransportationType,
];
const HUB_DH_REQUIRED_SETTINGS = [SuggestedSettingsEnum.RouteType, SuggestedSettingsEnum.OR];
const CUSTOM_ROUTE_REQUIRED_SETTINGS = [
  SuggestedSettingsEnum.RouteType,
  SuggestedSettingsEnum.StartLocation,
  SuggestedSettingsEnum.StartTime,
  SuggestedSettingsEnum.ArrivalHub,
];

const SETTINGS_VALIDATION_MAP: Record<RouteTypeSettingsEnum, SuggestedSettingsEnum[]> = {
  [RouteTypeSettingsEnum.Full]: FULL_ROUTE_REQUIRED_SETTINGS,
  [RouteTypeSettingsEnum.Hub_DH]: HUB_DH_REQUIRED_SETTINGS,
  [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: [
    ...CUSTOM_ROUTE_REQUIRED_SETTINGS,
    SuggestedSettingsEnum.OrganTransportationType,
  ],
  [RouteTypeSettingsEnum.Custom_TXC_Hub]: CUSTOM_ROUTE_REQUIRED_SETTINGS,
};

export const getResourceValidationError = (
  routeType: RouteTypeSettingsEnum,
  csResources: CaseResource<CaseClinicalSpecialistResource>[],
  deviceResources: CaseResource<CaseOCSConsoleResource>[],
): string[] => {
  if ([RouteTypeSettingsEnum.Custom_TXC_Hub, RouteTypeSettingsEnum.Custom_DH_TXC_Hub].includes(routeType)) {
    const hasCsResources = !!csResources.length;
    const hasDeviceResources = !!deviceResources.length;

    return [
      ...(hasCsResources ? [] : ['Missing CS on case']),
      ...(hasDeviceResources ? [] : ['Missing OCS Device on case']),
    ];
  }

  return [];
};

type ValidateSelectedSettingsReturnType = {
  isValid: boolean;
  errors: string[];
};

export const validateSelectedSettings = (
  list: SuggestedSettingsType[],
  csResources: CaseResource<CaseClinicalSpecialistResource>[],
  deviceResources: CaseResource<CaseOCSConsoleResource>[],
  disabledRouteTypes: Partial<Record<RouteTypeSettingsEnum, string>> = {},
): ValidateSelectedSettingsReturnType => {
  const routeType = list.find((s) => s.type === SuggestedSettingsEnum.RouteType)!.value;
  const validationRules = SETTINGS_VALIDATION_MAP[routeType];
  const disabledReason = disabledRouteTypes[routeType];
  const validationErrors = [
    ...(disabledReason ? [disabledReason] : []),
    ...getResourceValidationError(routeType, csResources, deviceResources),
  ];

  list.forEach((settingsItem) => {
    const isRequiredSetting = validationRules.includes(settingsItem.type);
    const hasValue = !!settingsItem.value;
    const errorMessage = SUGGESTED_SETTINGS_ERRORS_MAP[settingsItem.type] || '';

    if (isRequiredSetting && !hasValue && errorMessage) {
      validationErrors.push(errorMessage);

      return;
    }

    const isStartTimeInPast =
      settingsItem.type === SuggestedSettingsEnum.StartTime &&
      hasValue &&
      settingsItem.value.toDate().getTime() < Date.now();

    if (isRequiredSetting && isStartTimeInPast) {
      validationErrors.push(START_TIME_IN_PAST_ERROR);
    }
  });

  return {
    isValid: !validationErrors.length,
    errors: validationErrors,
  };
};
